import { apiCall } from '../utils/api';
import { queryCache, BLOG_LIST_TTL, BLOG_META_TTL, BLOG_DETAIL_TTL, MY_BLOGS_TTL } from './queryCache';

const BLOG_VIEWER_STORAGE_KEY = 'race-next-step-blog-viewer';

const formatCompactNumber = (value = 0) => {
  const number = Number(value || 0);
  if (number >= 1000000) {
    return `${(number / 1000000).toFixed(1).replace('.0', '')}M`;
  }
  if (number >= 1000) {
    return `${(number / 1000).toFixed(1).replace('.0', '')}K`;
  }
  return String(number);
};

export const formatViewsLabel = (value = 0) => `${formatCompactNumber(value)} views`;
export const formatCompactMetric = formatCompactNumber;
export const DEFAULT_BLOG_CATEGORY_OPTIONS = [
  { value: 'College Guide', label: 'College Guide' },
  { value: 'Coding', label: 'Coding' },
  { value: 'Placements', label: 'Placements' },
  { value: 'Exams', label: 'Exams' },
  { value: 'Student Life', label: 'Student Life' },
  { value: 'Career', label: 'Career' },
  { value: 'General', label: 'General' }
];

const formatDateLabel = (value) => {
  if (!value) {
    return 'Recently';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Recently';
  }

  return date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const estimateReadTime = (wordCount = 0) => `${Math.max(1, Math.ceil((Number(wordCount) || 0) / 200))} min`;

// Injects Cloudinary transformation params into a Cloudinary URL
// variant: 'card' → small optimized, 'detail' → full width optimized
const getOptimizedImageUrl = (url, variant = 'card') => {
  if (!url || !url.includes('res.cloudinary.com')) return url;
  const transforms = variant === 'detail'
    ? 'w_900,f_auto,q_auto'
    : 'w_400,h_280,c_fill,f_auto,q_auto';
  return url.replace('/upload/', `/upload/${transforms}/`);
};

export const mapBlogApiToUi = (blog, index = 0) => ({
  id: blog?._id || blog?.slug || `blog-${index}`,
  _id: blog?._id || null,
  slug: blog?.slug || '',
  title: blog?.title || '',
  category: blog?.category?.label || 'General',
  categoryKey: blog?.category?.key || 'general',
  author: blog?.author?.fullName || 'Race Next Step',
  readTime: estimateReadTime(blog?.content?.wordCount || blog?.wordCount || 0),
  publishDate: formatDateLabel(blog?.publishedAt || blog?.createdAt),
  views: formatViewsLabel(blog?.metrics?.viewCount || 0),
  likes: blog?.metrics?.likeCount || 0,
  comments: blog?.metrics?.commentCount || 0,
  description: blog?.excerpt || '',
  excerpt: blog?.excerpt || '',
  trendingRank: String(index + 1).padStart(2, '0'),
  image: getOptimizedImageUrl(blog?.coverImage?.secureUrl || blog?.coverImage?.url || '', 'card'),
  content: blog?.content?.html || '',
  metrics: blog?.metrics || { likeCount: 0, commentCount: 0, viewCount: 0 },
  engagement: blog?.engagement || { trendingScore: 0 },
  tags: Array.isArray(blog?.tags) ? blog.tags : []
});

const mapCommentApiToUi = (comment) => ({
  id: comment?._id || '',
  author: comment?.author?.fullName || 'User',
  text: comment?.body?.text || '',
  createdAt: comment?.createdAt || null
});

const mapStatusToTone = (status = '') => {
  if (status === 'published') {
    return 'published';
  }

  if (status === 'pending_approval') {
    return 'pending';
  }

  if (status === 'rejected') {
    return 'rejected';
  }

  return 'draft';
};

const mapStatusToLabel = (status = '') => {
  if (status === 'published') {
    return 'Published';
  }

  if (status === 'pending_approval') {
    return 'Pending Review';
  }

  if (status === 'rejected') {
    return 'Rejected';
  }

  return 'Draft';
};

const formatMyBlogDateLabel = (blog) => {
  const status = blog?.status || 'draft';
  const sourceDate = blog?.publishedAt || blog?.approval?.submittedAt || blog?.updatedAt || blog?.createdAt;
  const dateLabel = formatDateLabel(sourceDate);

  if (status === 'published') {
    return `Published on ${dateLabel}`;
  }

  if (status === 'pending_approval') {
    return `Submitted on ${dateLabel}`;
  }

  if (status === 'rejected') {
    return `Reviewed on ${dateLabel}`;
  }

  return `Updated on ${dateLabel}`;
};

export const mapMyBlogApiToUi = (blog, index = 0) => ({
  id: blog?._id || `my-blog-${index}`,
  _id: blog?._id || null,
  slug: blog?.slug || '',
  rawStatus: blog?.status || 'draft',
  title: blog?.title || '',
  status: mapStatusToLabel(blog?.status),
  statusTone: mapStatusToTone(blog?.status),
  dateLabel: formatMyBlogDateLabel(blog),
  readTime: `${Math.max(1, blog?.readTimeMinutes || Math.ceil((blog?.wordCount || 0) / 200) || 1)} min read`,
  description: blog?.excerpt || '',
  tags: Array.isArray(blog?.tags) ? blog.tags : [],
  likes: formatCompactNumber(blog?.metrics?.likeCount || 0),
  comments: formatCompactNumber(blog?.metrics?.commentCount || 0),
  views: formatCompactNumber(blog?.metrics?.viewCount || 0),
  insight:
    blog?.status === 'pending_approval'
      ? 'Under Review'
      : blog?.status === 'rejected'
        ? 'Needs revision'
        : blog?.status === 'draft'
          ? 'Draft in progress'
          : `${formatCompactNumber(blog?.metrics?.viewCount || 0)} total views`,
  insightSubtext:
    blog?.status === 'pending_approval'
      ? 'Usually takes 24-48 hours'
      : blog?.status === 'rejected'
        ? blog?.approval?.rejectionReason || 'Check feedback before resubmitting'
        : blog?.status === 'draft'
          ? 'Save and submit when ready'
          : `${formatCompactNumber(blog?.metrics?.likeCount || 0)} likes and ${formatCompactNumber(blog?.metrics?.commentCount || 0)} comments`,
  actionLabel: blog?.status === 'published' ? 'View Blog' : 'Open Draft',
  image: getOptimizedImageUrl(blog?.coverImage?.secureUrl || blog?.coverImage?.url || '', 'card'),
  category: blog?.category?.label || 'General',
  content: blog?.content?.html || '',
  metrics: blog?.metrics || { likeCount: 0, commentCount: 0, viewCount: 0 },
  approval: blog?.approval || {},
  raw: blog
});

async function readJson(response) {
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(data?.error || data?.message || 'Request failed');
  }
  return data;
}

export async function fetchBlogs(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.set(key, value);
    }
  });
  const cacheKey = `blogs:${query.toString()}`;
  const cached = queryCache.get(cacheKey);
  if (cached) return cached;

  const response = await apiCall(`/api/blogs${query.toString() ? `?${query.toString()}` : ''}`);
  const data = await readJson(response);
  const fallbackPage = Number(params.page) || 1;
  const fallbackLimit = Number(params.limit) || 9;
  const total = Number(data?.pagination?.total ?? data?.items?.length ?? 0);

  const result = {
    ...data,
    items: Array.isArray(data?.items) ? data.items.map(mapBlogApiToUi) : [],
    pagination: {
      page: Number(data?.pagination?.page) || fallbackPage,
      limit: Number(data?.pagination?.limit) || fallbackLimit,
      total,
      totalPages:
        Number(data?.pagination?.totalPages) || Math.max(1, Math.ceil(total / fallbackLimit))
    }
  };
  queryCache.set(cacheKey, result, BLOG_LIST_TTL);
  return result;
}

export async function fetchBlogsMeta(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.set(key, value);
    }
  });
  const cacheKey = `blogs-meta:${query.toString()}`;
  const cached = queryCache.get(cacheKey);
  if (cached) return cached;

  const response = await apiCall(`/api/blogs/meta${query.toString() ? `?${query.toString()}` : ''}`);
  const data = await readJson(response);

  const result = {
    ...data,
    totals: { allBlogs: Number(data?.totals?.allBlogs) || 0 },
    categories: Array.isArray(data?.categories) ? data.categories : [],
    trendingBlogs: Array.isArray(data?.trendingBlogs)
      ? data.trendingBlogs.map((blog, index) => mapBlogApiToUi(blog, index))
      : [],
    trendingTags: Array.isArray(data?.trendingTags) ? data.trendingTags : [],
    availableSorts: Array.isArray(data?.availableSorts) ? data.availableSorts : []
  };
  queryCache.set(cacheKey, result, BLOG_META_TTL);
  return result;
}

export async function fetchBlogDetail(slug, options = {}) {
  const cacheKey = `blog-detail:${String(slug || '').trim().toLowerCase()}`;
  if (!options?.fresh) {
    const cached = queryCache.get(cacheKey);
    if (cached) return cached;
  }

  const response = await apiCall(`/api/blogs/${encodeURIComponent(slug)}?relatedLimit=8`);
  const data = await readJson(response);

  const mappedBlog = {
    ...mapBlogApiToUi(data),
    image: getOptimizedImageUrl(data?.coverImage?.secureUrl || data?.coverImage?.url || '', 'detail'),
    content: data?.content?.html || '<p>Content coming soon.</p>',
    relatedBlogs: Array.isArray(data?.relatedBlogs)
      ? data.relatedBlogs.map((blog, index) => mapBlogApiToUi(blog, index))
      : []
  };

  queryCache.set(cacheKey, mappedBlog, BLOG_DETAIL_TTL);
  return mappedBlog;
}

export async function fetchBlogComments(slug, params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.set(key, value);
    }
  });

  const cacheKey = `blog-comments:${String(slug || '').trim().toLowerCase()}?${query.toString()}`;
  const cached = queryCache.get(cacheKey);
  if (cached) return cached;

  const response = await apiCall(`/api/blogs/${encodeURIComponent(slug)}/comments${query.toString() ? `?${query.toString()}` : ''}`);
  const data = await readJson(response);

  const mappedComments = {
    items: Array.isArray(data?.items) ? data.items.map(mapCommentApiToUi) : [],
    pagination: {
      page: Number(data?.pagination?.page) || 1,
      limit: Number(data?.pagination?.limit) || 20,
      total: Number(data?.pagination?.total) || 0,
      totalPages: Number(data?.pagination?.totalPages) || 1
    }
  };

  queryCache.set(cacheKey, mappedComments, BLOG_LIST_TTL);
  return mappedComments;
}

export async function fetchMyLikeStatus(slug) {
  const response = await apiCall(`/api/blogs/${encodeURIComponent(slug)}/likes/me`);
  const data = await readJson(response);

  return {
    liked: Boolean(data?.liked),
    metrics: data?.metrics || { likeCount: 0, commentCount: 0, viewCount: 0 }
  };
}

export async function toggleBlogLike(slug) {
  const response = await apiCall(`/api/blogs/${encodeURIComponent(slug)}/likes/toggle`, {
    method: 'POST'
  });
  const data = await readJson(response);
  queryCache.invalidate(`blog-detail:${String(slug || '').trim().toLowerCase()}`);
  return data;
}

export async function postBlogComment(slug, text) {
  const response = await apiCall(`/api/blogs/${encodeURIComponent(slug)}/comments`, {
    method: 'POST',
    body: JSON.stringify({ text })
  });
  const data = await readJson(response);

  const mappedCommentResponse = {
    ...data,
    comment: data?.comment ? mapCommentApiToUi(data.comment) : null
  };

  queryCache.invalidatePrefix(`blog-comments:${String(slug || '').trim().toLowerCase()}`);
  return mappedCommentResponse;
}

function getViewerHash() {
  if (typeof window === 'undefined') {
    return 'server-viewer';
  }

  const existingViewerId = window.localStorage.getItem(BLOG_VIEWER_STORAGE_KEY);
  if (existingViewerId) {
    return existingViewerId;
  }

  const nextViewerId = `viewer-${Math.random().toString(36).slice(2, 10)}`;
  window.localStorage.setItem(BLOG_VIEWER_STORAGE_KEY, nextViewerId);
  return nextViewerId;
}

export async function trackBlogView(slug) {
  const response = await apiCall(`/api/blogs/${encodeURIComponent(slug)}/views`, {
    method: 'POST',
    body: JSON.stringify({
      viewerHash: getViewerHash(),
      dateBucket: new Date().toISOString()
    })
  });
  return readJson(response);
}

export function primeBlogDetailCache(slug, blogData) {
  const cacheKey = `blog-detail:${String(slug || '').trim().toLowerCase()}`;
  if (!cacheKey || !blogData) return;
  queryCache.set(cacheKey, blogData, BLOG_DETAIL_TTL);
}

export function syncBlogDetailMetricsCache(slug, metrics = {}) {
  const cacheKey = `blog-detail:${String(slug || '').trim().toLowerCase()}`;
  const current = queryCache.get(cacheKey);
  if (!current) return;
  queryCache.set(cacheKey, {
    ...current,
    metrics: { ...(current?.metrics || {}), ...metrics }
  }, BLOG_DETAIL_TTL);
}

export async function fetchMyBlogs(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.set(key, value);
    }
  });
  const cacheKey = `my-blogs:${query.toString()}`;
  const cached = queryCache.get(cacheKey);
  if (cached) return cached;

  const response = await apiCall(`/api/my-blogs${query.toString() ? `?${query.toString()}` : ''}`);
  const data = await readJson(response);
  const fallbackPage = Number(params.page) || 1;
  const fallbackLimit = Number(params.limit) || 10;
  const total = Number(data?.pagination?.total ?? data?.items?.length ?? 0);

  const result = {
    items: Array.isArray(data?.items) ? data.items.map(mapMyBlogApiToUi) : [],
    pagination: {
      page: Number(data?.pagination?.page) || fallbackPage,
      limit: Number(data?.pagination?.limit) || fallbackLimit,
      total,
      totalPages: Number(data?.pagination?.totalPages) || Math.max(1, Math.ceil(total / fallbackLimit))
    }
  };
  queryCache.set(cacheKey, result, MY_BLOGS_TTL);
  return result;
}

export async function fetchMyBlogsSummary() {
  const cacheKey = 'my-blogs-summary';
  const cached = queryCache.get(cacheKey);
  if (cached) return cached;

  const response = await apiCall('/api/my-blogs/summary');
  const data = await readJson(response);

  const result = {
    totalBlogs: Number(data?.totalBlogs) || 0,
    draftBlogs: Number(data?.draftBlogs) || 0,
    pendingBlogs: Number(data?.pendingBlogs) || 0,
    publishedBlogs: Number(data?.publishedBlogs) || 0,
    rejectedBlogs: Number(data?.rejectedBlogs) || 0,
    totalLikes: Number(data?.totalLikes) || 0,
    totalComments: Number(data?.totalComments) || 0,
    totalViews: Number(data?.totalViews) || 0,
    averageEngagementRate: Number(data?.averageEngagementRate) || 0
  };
  queryCache.set(cacheKey, result, MY_BLOGS_TTL);
  return result;
}

export async function fetchMyBlog(blogId) {
  const response = await apiCall(`/api/my-blogs/${encodeURIComponent(blogId)}`);
  return readJson(response);
}

export async function deleteMyBlog(blogId) {
  const response = await apiCall(`/api/my-blogs/${encodeURIComponent(blogId)}`, {
    method: 'DELETE'
  });
  const data = await readJson(response);
  queryCache.invalidatePrefix('my-blogs');
  return data;
}

export async function createMyBlog(payload) {
  const response = await apiCall('/api/my-blogs', {
    method: 'POST',
    body: JSON.stringify(payload)
  });
  const data = await readJson(response);
  queryCache.invalidatePrefix('my-blogs');
  return data;
}

export async function updateMyBlog(blogId, payload) {
  const response = await apiCall(`/api/my-blogs/${encodeURIComponent(blogId)}`, {
    method: 'PUT',
    body: JSON.stringify(payload)
  });
  const data = await readJson(response);
  queryCache.invalidatePrefix('my-blogs');
  return data;
}

export async function submitMyBlog(blogId) {
  const response = await apiCall(`/api/my-blogs/${encodeURIComponent(blogId)}/submit`, {
    method: 'POST'
  });
  const data = await readJson(response);
  queryCache.invalidatePrefix('my-blogs');
  return data;
}

export async function createBlogCoverUploadSignature(file) {
  const response = await apiCall('/api/my-blogs/uploads/cover/signature', {
    method: 'POST',
    body: JSON.stringify({
      fileName: file?.name || '',
      fileType: file?.type || '',
      fileSize: Number(file?.size || 0)
    })
  });

  return readJson(response);
}

export async function uploadBlogCoverToCloudinary(file) {
  const uploadConfig = await createBlogCoverUploadSignature(file);
  const formData = new FormData();

  formData.append('file', file);
  formData.append('api_key', uploadConfig.apiKey);
  formData.append('timestamp', String(uploadConfig.timestamp));
  formData.append('signature', uploadConfig.signature);
  formData.append('folder', uploadConfig.folder);
  formData.append('public_id', uploadConfig.publicId);
  formData.append('eager', uploadConfig.eager);

  const response = await fetch(`https://api.cloudinary.com/v1_1/${uploadConfig.cloudName}/image/upload`, {
    method: 'POST',
    body: formData
  });

  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(data?.error?.message || 'Cloudinary upload failed');
  }

  return {
    publicId: data?.public_id || uploadConfig.publicId,
    url: data?.url || '',
    secureUrl: data?.secure_url || '',
    resourceType: data?.resource_type || 'image',
    format: data?.format || '',
    width: Number(data?.width || 0),
    height: Number(data?.height || 0),
    bytes: Number(data?.bytes || 0),
    folder: data?.folder || uploadConfig.folder,
    version: String(data?.version || ''),
    altText: '',
    originalFileName: file?.name || ''
  };
}
